<template>
    <div>
        <div class="flex flex-vertical-middle flex-wrap">
            <div class="spacer-10-bottom spacer-right-30">
                <span>Datum:&nbsp;</span>
                <span v-html="entryTimeString"></span>
            </div>

            <div v-if="entry.location_text_title" class="spacer-10-bottom spacer-right-30">
                <span>Ort:&nbsp;</span>
                <router-link v-if="entry.room_id" :to="{name: 'roommanagement-list', params: {'id': entry.room_id}}">
                    {{entry.location_text_title}}
                </router-link>
                <span v-else>{{entry.location_text_title}}</span>
            </div>

            <div v-if="entry.owner_user_id" class="spacer-10-bottom">
                <div class="flex flex-vertical-middle flex-wrap">
                    <span>Termin von:&nbsp;&nbsp;</span>
                    <person-box-user :id="entry.owner_user_id" class="tiny"/>
                </div>
            </div>
        </div>

        <div class="row" v-if="entry.institution_id">
            <div class="col-12 col-md-4">
                <span>Mandat:</span>
                <router-link v-if="!entry.institution_deleted" :to="'/contact/entry/view/id/' + entry.institution_id">
                    {{entry.institution_name}} ({{entry.institution_id}})
                </router-link>
                <span v-else>
                    {{entry.institution_name}}
                </span>
            </div>
        </div>

        <template v-if="entry.invitations && entry.invitations.length">
            <app-invitations v-model="entry.invitations" entry-type="Termin" :entry-title="entry.title"
                :response-url="'/appointment/entry/response/id/' + entry.id" :show-popup="decision" class="portuna-box-full-content-width"
                :is-popup="isPopup" :showStatus="!entry.force_approve" :allow-like="!entry.force_approve"/>
            <hr class="no-margin-top portuna-box-full-content-width">
        </template>

        <div v-if="entry.description" class="default-margin-top message-content fr-view">
            <div><strong>Beschreibung</strong></div>
            <div class="spacer-5" v-html="entry.description"></div>
        </div>

        <div v-if="entry.description_additional" class="default-margin-top message-content fr-view">
            <div><strong>Zusätzliche Informationen:</strong></div>
            <div class="spacer-5" v-html="entry.description_additional"></div>
        </div>

        <div v-if="entry.files && entry.files.length" class="spacer-15">
            <div><strong>Dokumente</strong></div>
            <file-list :files="entry.files">
                <template #additional>
                    <app-popup-download
                        v-if="entry.files.length > 1"
                        :create-route="`/appointment/entry/create-zip-files/id/${entry.id}`"
                        :download-route="'/appointment/entry/download-zip-file'"
                        class="col-12 col-lg-6 flex-vertical-middle">
                        <template #link="{downloadFiles}">
                            <a class="button button-secondary button-block spacer-10" @click="downloadFiles">
                                <i class="material-icons" aria-hidden="true" role="img">file_download</i>
                                <span>Alle Dateien herunterladen</span>
                            </a>
                        </template>
                    </app-popup-download>
                </template>
            </file-list>
        </div>

        <div v-if="isPopup && notes && notes.length" class="spacer-15">
            <div><strong>Persönliche Notizen</strong></div>

            <div>
                <div v-for="(note, index) in notesOrdered" :key="note.id" class="note">
                    <hr v-if="index" class="no-margin"/>

                    <div class="flex margin-top-10 margin-bottom-10">
                        <person-box-user :id="note.creator" class="small margin-right-10">
                            {{note.created}} Uhr
                        </person-box-user>

                        <div v-html="note.note" class="flex-grow"/>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import axios from 'axios';
    import AppInvitations from '@/components/AppInvitations.vue';
    import AppPopupDownload from '@/components/AppPopupDownload.vue';
    import FileList from '@/components/FileList.vue';
    import PersonBoxUser from '@/components/PersonBoxUser.vue';
    import {getEntryTimeString} from '@/library/modules/calendar/calendar';
    import {XHR_STATUS_DEFAULT} from '@/utils/portuna';
    import dateHelper from '@/utils/portuna/dateHelper';
    import {useRoute} from 'vue-router';
    import {ref, computed, toRefs} from 'vue';

    const props = withDefaults(
        defineProps<{
            resource: CalendarResources;
            additionalData?: AdditionalCalendarData;
            entry: AppointmentEntryData;
            calendar: CurrentCalendarData | CalendarData;
            decision?: Nullable<LikeStatus>;
        }>(),
        {
            additionalData: () => ({}),
            decision: null,
        },
    );

    const {
              entry,
              decision,
          } = toRefs(props);

    const route = useRoute();

    const notes = ref<CalendarEntryNote[]>([]);

    const entryTimeString = computed(() =>
    {
        return getEntryTimeString(entry.value!, false);
    });

    const isPopup = computed((): boolean =>
    {
        return route.name !== 'appointment-entry-view';
    });

    const notesOrdered = computed((): CalendarEntryNote[] =>
    {
        let notesOrdered: CalendarEntryNote[] = notes.value!.sort((a, b) => a.id - b.id);
        for (let orderedNote of notesOrdered)
        {
            orderedNote.created = dateHelper('d.m.Y, H:i', orderedNote.created) as string;
        }

        return notesOrdered;
    });

    const fetchNotes = () =>
    {
        notes.value! = [];

        axios
        .get(
            '/appointment/entry/fetch-notes/id/' +
            entry.value!.id +
            '/start/' +
            entry.value!.recurrence_start,
        )
        .then((response) =>
        {
            const {
                      data,
                      status,
                  } = response.data;

            if (status !== XHR_STATUS_DEFAULT)
            {
                throw response;
            }

            notes.value! = data.notes;
        });
    };

    if (isPopup.value)
    {
        fetchNotes();
    }
</script>